import PropTypes from 'prop-types'
import { useRef, useEffect, useState, useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'next-i18next'

// Internal components
import FormError from '../FormError/FormError'
import Svg from '../Svg/Svg'
import { UserContext } from '../../context/UserContext'

const revealClass = ' is-revealed'
const activeClass = ' is-active'

const FormItemSelect = (props) => {
  const { item, onFormItemChange, onFormItemLoad, groupedState, revealState, shouldValidateState } = props

  const { register, watch, errors, formState, trigger } = useFormContext()

  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED')

  // States
  const [isRequired, setIsRequired] = useState({ required: false })
  const [isGrouped, setIsGrouped] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const itemRef = useRef()
  const itemProps = item
  const itemValue = watch(item.name)
  const itemInputRef = register(item.name, isRequired)

  const createMarkup = (copy) => ({ __html: copy })

  useEffect(() => {
    if (!item.revealed && item.required) setIsRequired({ required: requiredErrorMessage })
  }, [])

  useEffect(() => {
    if (!itemValue) return false
    if (!isLoaded) onFormItemLoad({ itemProps, itemRef, itemValue })
    else onFormItemChange({ itemProps, itemRef, itemValue })
    setIsLoaded(true)
  }, [itemValue])

  useEffect(() => {
    if (!item.revealed || !item.required) return false
    setIsRequired({ required: revealState ? requiredErrorMessage : false })
    trigger()
  }, [revealState])

  useEffect(() => {
    if (!shouldValidateState) setIsRequired({ required: false })
  }, [shouldValidateState])

  useEffect(() => {
    if (item?.group && groupedState) {
      const itemLabel = item?.group?.label
      const groupedItem = groupedState.filter((str) => {
        return str.label.indexOf(itemLabel) >= 0
      })
      setIsGrouped(groupedItem[0].active)
    }
  }, [groupedState])

  return (
    <div
      ref={itemRef}
      className={`FormItem FormItem--${item.type}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
        revealState ? revealClass : ''
      }${!isGrouped ? '' : activeClass}`}
      data-reveal-target={item.revealed}
    >
      {item.radios.length &&
        item.radios.map((radio, index) => {
          return (
            <div className="FormItem-radioButton" key={`${item.name}_${radio.id}_${index}`}>
              <label className="FormItem-radioButtonLabel" htmlFor={`${item.name}_${radio.id}`}>
                <input
                  type="radio"
                  name={item.name}
                  id={`${item.name}_${radio.id}`}
                  className={`FormItem-input FormItem-input--${item.type}`}
                  value={radio.value}
                  defaultChecked={item.defaultChecked === radio.value ? item.defaultChecked : ''}
                  onChange={(e) => {
                    itemInputRef.onChange(e)
                  }}
                  ref={itemInputRef.ref}
                />
                <div className="FormItem-radioButtonIndicator">
                  <Svg id="icon-check" modifiers={['FormItem-radioButtonIcon']} />
                </div>
                <span
                  className="FormItem-radioButtonCopy"
                  dangerouslySetInnerHTML={createMarkup(!radio.transl ? radio.label : t(radio.transl))}
                ></span>
              </label>
            </div>
          )
        })}
      {formState.isSubmitted && <ErrorMessage errors={errors} name={item.name} as={<FormError />} />}
    </div>
  )
}

FormItemSelect.propTypes = {
  item: PropTypes.object,
  onFormItemChange: PropTypes.func,
  onFormItemLoad: PropTypes.func,
  revealState: PropTypes.any,
  groupedState: PropTypes.any,
  shouldValidateState: PropTypes.bool
}

export default FormItemSelect
